import { convertFromUTC, convertToUTC } from "utils/datetime";

export const FRACTION_STEPS = [15, 30, 60];

const EMPTY_UBICATION = "";

export function normalizeCounter(value) {
	const number = Number(value);
	if (!Number.isFinite(number) || number <= 0) return 0;
	return Math.min(999, Math.floor(number));
}

export function cellKey(functionId, shiftId, dayTypeId, ubicationId) {
	const ubication =
		ubicationId == null || ubicationId === "" ? EMPTY_UBICATION : Number(ubicationId);
	return `${Number(functionId)}:${Number(shiftId)}:${Number(dayTypeId)}:${ubication}`;
}

export function needUbicationId(need) {
	if (!need || !need.ubication || need.ubication.id == null) return null;
	return need.ubication.id;
}

export function readCounter(needs, functionId, shiftId, dayTypeId, ubicationId) {
	const key = cellKey(functionId, shiftId, dayTypeId, ubicationId);
	const found = (needs || []).find((need) => {
		if (!need.function || !need.shift || !need.day_type) return false;
		return (
			cellKey(need.function.id, need.shift.id, need.day_type.id, needUbicationId(need)) ===
			key
		);
	});
	return found ? normalizeCounter(found.counter) : 0;
}

export function clockSortMinutes(clock) {
	if (!clock || typeof clock !== "string") return null;
	const match = clock.match(/^(\d{1,2}):(\d{2})/);
	if (!match) return null;
	let hours = Number(match[1]);
	const minutes = Number(match[2]);
	if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;
	if (hours < 4) hours += 24;
	return hours * 60 + minutes;
}

export function shiftSortKey(shift) {
	const raw = shift && shift.time_intervals && shift.time_intervals[0]
		? shift.time_intervals[0].start
		: "";
	const local = (raw && convertFromUTC(raw)) || raw;
	const minutes = clockSortMinutes(local);
	return minutes == null ? 48 * 60 : minutes;
}

export function sortShifts(shifts) {
	return [...(shifts || [])].sort((a, b) => {
		const diff = shiftSortKey(a) - shiftSortKey(b);
		if (diff !== 0) return diff;
		return String(a.name || "").localeCompare(String(b.name || ""), "es");
	});
}

export function sortUbications(ubications) {
	return [...(ubications || [])].sort((a, b) =>
		String(a.name || "").localeCompare(String(b.name || ""), "es")
	);
}

export function orderedByCatalog(catalog, selected) {
	const selectedIds = new Set((selected || []).map((item) => item.id));
	const inCatalog = (catalog || []).filter((item) => selectedIds.has(item.id));
	const missing = (selected || []).filter(
		(item) => !(catalog || []).some((entry) => entry.id === item.id)
	);
	return [...inCatalog, ...missing];
}

export function filterCatalog(items, selected, query, labelOf) {
	const list = items || [];
	const text = (query || "").trim().toLowerCase();
	if (!text) return list;
	const selectedIds = new Set((selected || []).map((item) => item.id));
	return list.filter((item) => {
		if (selectedIds.has(item.id)) return true;
		const label = labelOf ? labelOf(item) : item.name;
		return String(label || "").toLowerCase().includes(text);
	});
}

/** Violeta de marca (#5b49fe). El 1 usa esta opacidad; el pico de la grilla llega a 1. */
export const CELL_FILL_LIGHTEST = 0.12;
export const CELL_FILL_RGB = "91, 73, 254";
const CELL_FILL_LIGHT_TEXT = 0.88;

export function gridPeak(needs, functionId) {
	return (needs || []).reduce((max, need) => {
		if (
			functionId != null &&
			Number(need.function && need.function.id) !== Number(functionId)
		) {
			return max;
		}
		return Math.max(max, normalizeCounter(need.counter));
	}, 0);
}

/**
 * 0 no lleva color. 1 es el violeta más claro.
 * El número más alto de la grilla (9 o más) queda al 100% de opacidad.
 */
export function cellFillAlpha(value, peak) {
	const n = normalizeCounter(value);
	if (n <= 0) return 0;
	const top = Math.max(normalizeCounter(peak), n);
	if (top <= 1) return CELL_FILL_LIGHTEST;
	const t = (n - 1) / (top - 1);
	return CELL_FILL_LIGHTEST + t * (1 - CELL_FILL_LIGHTEST);
}

export function cellFillStyle(value, peak) {
	const alpha = cellFillAlpha(value, peak);
	if (alpha <= 0) return null;
	return {
		backgroundColor: `rgba(${CELL_FILL_RGB}, ${Number(alpha.toFixed(3))})`,
		color: alpha >= CELL_FILL_LIGHT_TEXT ? "#ffffff" : "#0d0a2c",
	};
}

export function isGridReady({
	selectedFunctions,
	selectedShifts,
	selectedUbications,
	hasUbications,
	dayTypes,
}) {
	if (!selectedFunctions || !selectedFunctions.length) return false;
	if (!selectedShifts || !selectedShifts.length) return false;
	if (!dayTypes || !dayTypes.length) return false;
	if (hasUbications && (!selectedUbications || !selectedUbications.length)) return false;
	return true;
}

function indexCounters(needs) {
	const map = new Map();
	(needs || []).forEach((need) => {
		if (!need.function || !need.shift || !need.day_type) return;
		map.set(
			cellKey(need.function.id, need.shift.id, need.day_type.id, needUbicationId(need)),
			normalizeCounter(need.counter)
		);
	});
	return map;
}

function maxCounterByShiftDay(needs) {
	const map = new Map();
	(needs || []).forEach((need) => {
		if (!need.function || !need.shift || !need.day_type) return;
		const key = `${Number(need.function.id)}:${Number(need.shift.id)}:${Number(need.day_type.id)}`;
		const value = normalizeCounter(need.counter);
		map.set(key, Math.max(map.get(key) || 0, value));
	});
	return map;
}

export function rebuildNeeds({
	selectedFunctions = [],
	selectedShifts = [],
	selectedUbications = [],
	dayTypes = [],
	hasUbications = false,
	previousNeeds = [],
}) {
	if (!dayTypes.length) return previousNeeds || [];
	if (!selectedFunctions.length || !selectedShifts.length) return [];
	if (hasUbications && !selectedUbications.length) return previousNeeds || [];

	const indexed = indexCounters(previousNeeds);
	const collapsed = maxCounterByShiftDay(previousNeeds);
	const ubications = hasUbications ? selectedUbications : [null];
	const needs = [];

	selectedFunctions.forEach((fn) => {
		selectedShifts.forEach((shift) => {
			ubications.forEach((ubication) => {
				dayTypes.forEach((dayType) => {
					const ubicationId = ubication ? ubication.id : null;
					let counter = 0;

					if (hasUbications) {
						const exact = indexed.get(cellKey(fn.id, shift.id, dayType.id, ubicationId));
						if (exact != null) {
							counter = exact;
						} else if (selectedUbications.length === 1) {
							const bare = indexed.get(cellKey(fn.id, shift.id, dayType.id, null));
							counter = bare || 0;
						}
					} else {
						const bare = indexed.get(cellKey(fn.id, shift.id, dayType.id, null)) || 0;
						const maxLocated =
							collapsed.get(
								`${Number(fn.id)}:${Number(shift.id)}:${Number(dayType.id)}`
							) || 0;
						counter = Math.max(bare, maxLocated);
					}

					const item = {
						function: fn,
						shift,
						day_type: dayType,
						counter,
					};
					if (ubication) item.ubication = ubication;
					needs.push(item);
				});
			});
		});
	});

	return needs;
}

export function setCellValues(needs, updates) {
	const byKey = new Map();
	(updates || []).forEach((update) => {
		byKey.set(
			cellKey(update.functionId, update.shiftId, update.dayTypeId, update.ubicationId),
			normalizeCounter(update.value)
		);
	});

	return (needs || []).map((need) => {
		if (!need.function || !need.shift || !need.day_type) return need;
		const key = cellKey(
			need.function.id,
			need.shift.id,
			need.day_type.id,
			needUbicationId(need)
		);
		if (!byKey.has(key)) return need;
		const counter = byKey.get(key);
		if (counter === normalizeCounter(need.counter)) return need;
		return { ...need, counter };
	});
}

export function functionTotal(needs, functionId) {
	return (needs || []).reduce((sum, need) => {
		if (!need.function || Number(need.function.id) !== Number(functionId)) return sum;
		return sum + normalizeCounter(need.counter);
	}, 0);
}

export function ubicationTotal(needs, functionId, ubicationId) {
	return (needs || []).reduce((sum, need) => {
		if (!need.function || Number(need.function.id) !== Number(functionId)) return sum;
		if (Number(needUbicationId(need)) !== Number(ubicationId)) return sum;
		return sum + normalizeCounter(need.counter);
	}, 0);
}

export function buildGridRows({ shifts, ubications, hasUbications }) {
	const sortedShifts = sortShifts(shifts);
	if (!hasUbications) {
		return sortedShifts.map((shift) => ({ kind: "shift", shift, ubication: null }));
	}

	const rows = [];
	sortUbications(ubications).forEach((ubication) => {
		rows.push({ kind: "ubication", ubication });
		sortedShifts.forEach((shift) => {
			rows.push({ kind: "shift", shift, ubication });
		});
	});
	return rows;
}

export function shiftRows(rows) {
	return (rows || []).filter((row) => row.kind === "shift");
}

export function slotRows(rows) {
	return (rows || []).filter((row) => row.kind === "shift" || row.kind === "fraction");
}

export function columnClipboard(needs, { functionId, dayTypeId, rows }) {
	return {
		kind: "column",
		entries: slotRows(rows).map((row) => {
			const ubicationId = row.ubication ? row.ubication.id : null;
			const fractionKey = row.fraction ? row.fraction.key : null;
			return {
				shiftId: row.shift ? row.shift.id : null,
				fractionKey,
				ubicationId,
				value: fractionKey
					? readFractionCounter(needs, functionId, fractionKey, dayTypeId, ubicationId)
					: readCounter(needs, functionId, row.shift.id, dayTypeId, ubicationId),
			};
		}),
	};
}

export function applyColumnClipboard(needs, { functionId, dayTypeId, clipboard }) {
	if (!clipboard || clipboard.kind !== "column") return needs;
	return applySlotUpdates(
		needs,
		clipboard.entries.map((entry) => ({
			functionId,
			shiftId: entry.shiftId,
			fractionKey: entry.fractionKey,
			dayTypeId,
			ubicationId: entry.ubicationId,
			value: entry.value,
		}))
	);
}

export function rowClipboard(needs, { functionId, shiftId, fractionKey, ubicationId, dayTypes }) {
	return {
		kind: "row",
		entries: (dayTypes || []).map((dayType) => ({
			dayTypeId: dayType.id,
			value: fractionKey
				? readFractionCounter(needs, functionId, fractionKey, dayType.id, ubicationId)
				: readCounter(needs, functionId, shiftId, dayType.id, ubicationId),
		})),
	};
}

export function applyRowClipboard(needs, { functionId, shiftId, fractionKey, ubicationId, clipboard }) {
	if (!clipboard || clipboard.kind !== "row") return needs;
	return applySlotUpdates(
		needs,
		clipboard.entries.map((entry) => ({
			functionId,
			shiftId,
			fractionKey,
			dayTypeId: entry.dayTypeId,
			ubicationId,
			value: entry.value,
		}))
	);
}

export function toSubmitPayload(needs) {
	const curated = [];
	(needs || []).forEach((need) => {
		const counter = normalizeCounter(need.counter);
		if (counter <= 0 || !need.function || !need.day_type) return;
		const item = {
			counter,
			day_type_id: need.day_type.id,
			function_id: need.function.id,
		};
		if (need.shift && need.shift.id) {
			item.shift_id = need.shift.id;
		} else if (need.time_interval) {
			const range = intervalLocalRange(need.time_interval);
			const timeTypeId =
				need.time_interval.time_type_id ||
				(need.time_interval.time_type && need.time_interval.time_type.id);
			if (!range || !timeTypeId) return;
			item.time_interval = {
				start: convertToUTC(range.start),
				end: convertToUTC(range.end),
				time_type_id: Number(timeTypeId),
			};
		} else {
			return;
		}
		if (need.ubication) item.ubication_id = need.ubication.id;
		curated.push(item);
	});
	return curated;
}

export function isIntervalNeed(need) {
	return Boolean(need && need.time_interval && !(need.shift && need.shift.id));
}

export function isShiftNeed(need) {
	return Boolean(need && need.shift && need.shift.id);
}

export function parseLocalClock(clock) {
	if (!clock || typeof clock !== "string") return null;
	const match = clock.match(/^(\d{1,2}):(\d{2})/);
	if (!match) return null;
	const hours = Number(match[1]);
	const minutes = Number(match[2]);
	if (hours > 23 || minutes > 59) return null;
	return hours * 60 + minutes;
}

function clockParts(clock) {
	if (!clock || typeof clock !== "string") return null;
	const match = clock.match(/^(\d{1,2}):(\d{2})/);
	if (!match) return null;
	return { hour: match[1].padStart(2, "0"), minute: match[2] };
}

function hourDigitsComplete(digits) {
	if (!digits) return false;
	if (digits.length >= 2) return true;
	return Number(digits) >= 3;
}

function minuteDigitsComplete(digits) {
	if (!digits) return false;
	if (digits.length >= 2) return true;
	return Number(digits) >= 6;
}

export function advanceClockEdit(
	edit,
	{ field, previous, next, digit, key, minutesEditable }
) {
	const nxt = clockParts(next);
	if (!digit) {
		const spinner = key === "ArrowUp" || key === "ArrowDown";
		const cleared = key === "Backspace" || key === "Delete" || !nxt;
		return {
			edit: edit && edit.field === field ? edit : null,
			commit: Boolean(nxt) && !spinner && !cleared,
		};
	}
	if (!nxt) return { edit, commit: false };

	const prev = clockParts(previous);
	const hourChanged = !prev || prev.hour !== nxt.hour;
	const minuteChanged = !prev || prev.minute !== nxt.minute;
	if (hourChanged && minuteChanged) return { edit: null, commit: true };

	const state =
		edit && edit.field === field
			? { ...edit }
			: { field, hourDigits: "", minuteDigits: "", hourDone: false, minuteDone: false };

	if (hourChanged) {
		state.hourDigits += digit;
		state.hourDone = hourDigitsComplete(state.hourDigits);
		state.minuteDigits = "";
		state.minuteDone = false;
	} else if (minuteChanged) {
		state.hourDone = true;
		state.minuteDigits += digit;
		state.minuteDone = minuteDigitsComplete(state.minuteDigits);
	} else {
		return { edit: state, commit: false };
	}

	return {
		edit: state,
		commit: state.hourDone && (state.minuteDone || !minutesEditable),
	};
}

export function formatClock(totalMinutes) {
	const minutes = ((totalMinutes % 1440) + 1440) % 1440;
	const hours = Math.floor(minutes / 60);
	const mins = minutes % 60;
	return `${String(hours).padStart(2, "0")}:${String(mins).padStart(2, "0")}`;
}

function localClock(value) {
	if (!value || typeof value !== "string") return "";
	if (value.includes("Z")) return convertFromUTC(value) || "";
	const match = value.match(/^(\d{1,2}):(\d{2})/);
	if (!match) return "";
	return `${match[1].padStart(2, "0")}:${match[2]}`;
}

export function intervalLocalRange(interval) {
	if (!interval) return null;
	const start = localClock(interval.start);
	const end = localClock(interval.end);
	if (!start || !end) return null;
	return { start, end, key: `${start}-${end}` };
}

export function buildFractions(start, end, stepMinutes) {
	const startMin = parseLocalClock(start);
	const endMin = parseLocalClock(end);
	if (startMin == null || endMin == null || endMin <= startMin) return [];
	if (!FRACTION_STEPS.includes(stepMinutes)) return [];

	const fractions = [];
	for (let cursor = startMin; cursor < endMin; ) {
		const next = Math.min(cursor + stepMinutes, endMin);
		const startClock = formatClock(cursor);
		const endClock = formatClock(next);
		fractions.push({
			start: startClock,
			end: endClock,
			key: `${startClock}-${endClock}`,
		});
		if (next <= cursor) break;
		cursor = next;
	}
	return fractions;
}

export function inferFractionSetup(needs) {
	const ranges = (needs || [])
		.filter(isIntervalNeed)
		.map((need) => intervalLocalRange(need.time_interval))
		.filter(Boolean);

	if (!ranges.length) {
		return { start: "08:00", end: "20:00", minutes: 60 };
	}

	const startMin = Math.min(...ranges.map((range) => parseLocalClock(range.start)));
	const endMin = Math.max(...ranges.map((range) => parseLocalClock(range.end)));
	const durations = ranges.map((range) => {
		const diff = parseLocalClock(range.end) - parseLocalClock(range.start);
		return diff > 0 ? diff : diff + 1440;
	});
	const uniform = durations.every((duration) => duration === durations[0]);
	const minutes = uniform && FRACTION_STEPS.includes(durations[0]) ? durations[0] : 60;

	return {
		start: formatClock(startMin),
		end: formatClock(endMin),
		minutes,
	};
}

export function fractionsFromNeeds(needs) {
	const seen = new Set();
	const fractions = [];
	(needs || []).forEach((need) => {
		if (!isIntervalNeed(need)) return;
		const range = intervalLocalRange(need.time_interval);
		if (!range || seen.has(range.key)) return;
		seen.add(range.key);
		fractions.push(range);
	});
	return fractions.sort(
		(a, b) => parseLocalClock(a.start) - parseLocalClock(b.start)
	);
}

export function fractionWindowFromNeeds(needs) {
	const setup = inferFractionSetup(needs);
	const saved = fractionsFromNeeds(needs);
	if (!saved.length) return { ...setup, fractions: [] };

	const step = parseLocalClock(saved[0].end) - parseLocalClock(saved[0].start);
	const aligned =
		step > 0 &&
		FRACTION_STEPS.includes(step) &&
		saved.every(
			(fraction) => parseLocalClock(fraction.end) - parseLocalClock(fraction.start) === step
		);
	if (!aligned) return { ...setup, fractions: saved };

	const generated = buildFractions(setup.start, setup.end, step);
	const keys = new Set(generated.map((fraction) => fraction.key));
	if (!generated.length || !saved.every((fraction) => keys.has(fraction.key))) {
		return { ...setup, fractions: saved };
	}

	return {
		start: setup.start,
		end: setup.end,
		minutes: step,
		fractions: generated,
	};
}

export function fractionCellKey(functionId, fractionKey, dayTypeId, ubicationId) {
	const ubication =
		ubicationId == null || ubicationId === "" ? EMPTY_UBICATION : Number(ubicationId);
	return `${Number(functionId)}:${fractionKey}:${Number(dayTypeId)}:${ubication}`;
}

function timeTypeIdOf(need) {
	if (!need || !need.time_interval) return null;
	return (
		need.time_interval.time_type_id ||
		(need.time_interval.time_type && need.time_interval.time_type.id) ||
		null
	);
}

export function readFractionCounter(needs, functionId, fractionKey, dayTypeId, ubicationId) {
	const key = fractionCellKey(functionId, fractionKey, dayTypeId, ubicationId);
	const found = (needs || []).find((need) => {
		if (!isIntervalNeed(need) || !need.function || !need.day_type) return false;
		const range = intervalLocalRange(need.time_interval);
		if (!range) return false;
		return (
			fractionCellKey(need.function.id, range.key, need.day_type.id, needUbicationId(need)) ===
			key
		);
	});
	return found ? normalizeCounter(found.counter) : 0;
}

function indexFractionCounters(needs) {
	const map = new Map();
	(needs || []).forEach((need) => {
		if (!isIntervalNeed(need) || !need.function || !need.day_type) return;
		const range = intervalLocalRange(need.time_interval);
		if (!range) return;
		map.set(
			fractionCellKey(need.function.id, range.key, need.day_type.id, needUbicationId(need)),
			{
				counter: normalizeCounter(need.counter),
				timeTypeId: timeTypeIdOf(need),
				timeType: need.time_interval.time_type,
			}
		);
	});
	return map;
}

function maxFractionCounterByDay(needs) {
	const map = new Map();
	(needs || []).forEach((need) => {
		if (!isIntervalNeed(need) || !need.function || !need.day_type) return;
		const range = intervalLocalRange(need.time_interval);
		if (!range) return;
		const key = `${Number(need.function.id)}:${range.key}:${Number(need.day_type.id)}`;
		const value = normalizeCounter(need.counter);
		const current = map.get(key);
		if (!current || value >= current.counter) {
			map.set(key, {
				counter: value,
				timeTypeId: timeTypeIdOf(need),
				timeType: need.time_interval.time_type,
			});
		}
	});
	return map;
}

export function rebuildFractionNeeds({
	selectedFunctions = [],
	fractions = [],
	selectedUbications = [],
	dayTypes = [],
	hasUbications = false,
	previousNeeds = [],
	timeTypeId = null,
}) {
	if (!dayTypes.length || !fractions.length) return (previousNeeds || []).filter(isIntervalNeed);
	if (!selectedFunctions.length) return [];
	if (hasUbications && !selectedUbications.length) {
		return (previousNeeds || []).filter(isIntervalNeed);
	}

	const indexed = indexFractionCounters(previousNeeds);
	const collapsed = maxFractionCounterByDay(previousNeeds);
	const ubications = hasUbications ? selectedUbications : [null];
	const needs = [];

	selectedFunctions.forEach((fn) => {
		fractions.forEach((fraction) => {
			ubications.forEach((ubication) => {
				dayTypes.forEach((dayType) => {
					const ubicationId = ubication ? ubication.id : null;
					let counter = 0;
					let keptTypeId = timeTypeId;
					let keptType = null;

					const take = (entry) => {
						if (!entry) return;
						counter = entry.counter;
						if (entry.timeTypeId) keptTypeId = entry.timeTypeId;
						if (entry.timeType) keptType = entry.timeType;
					};

					if (hasUbications) {
						const exact = indexed.get(
							fractionCellKey(fn.id, fraction.key, dayType.id, ubicationId)
						);
						if (exact) {
							take(exact);
						} else if (selectedUbications.length === 1) {
							take(indexed.get(fractionCellKey(fn.id, fraction.key, dayType.id, null)));
						}
					} else {
						const bare = indexed.get(fractionCellKey(fn.id, fraction.key, dayType.id, null));
						const maxLocated = collapsed.get(
							`${Number(fn.id)}:${fraction.key}:${Number(dayType.id)}`
						);
						if ((bare && bare.counter) >= ((maxLocated && maxLocated.counter) || 0)) {
							take(bare);
						} else {
							take(maxLocated);
						}
						if (!bare && !maxLocated) counter = 0;
					}

					const item = {
						function: fn,
						day_type: dayType,
						counter,
						time_interval: {
							start: fraction.start,
							end: fraction.end,
						},
					};
					if (keptTypeId) item.time_interval.time_type_id = Number(keptTypeId);
					if (keptType) item.time_interval.time_type = keptType;
					if (ubication) item.ubication = ubication;
					needs.push(item);
				});
			});
		});
	});

	return needs;
}

export function setFractionCellValues(needs, updates) {
	const byKey = new Map();
	(updates || []).forEach((update) => {
		if (!update.fractionKey) return;
		byKey.set(
			fractionCellKey(update.functionId, update.fractionKey, update.dayTypeId, update.ubicationId),
			normalizeCounter(update.value)
		);
	});
	if (!byKey.size) return needs;

	return (needs || []).map((need) => {
		if (!isIntervalNeed(need) || !need.function || !need.day_type) return need;
		const range = intervalLocalRange(need.time_interval);
		if (!range) return need;
		const key = fractionCellKey(
			need.function.id,
			range.key,
			need.day_type.id,
			needUbicationId(need)
		);
		if (!byKey.has(key)) return need;
		const counter = byKey.get(key);
		if (counter === normalizeCounter(need.counter)) return need;
		return { ...need, counter };
	});
}

export function applySlotUpdates(needs, updates) {
	const shiftUpdates = [];
	const fractionUpdates = [];
	(updates || []).forEach((update) => {
		if (update.fractionKey) fractionUpdates.push(update);
		else shiftUpdates.push(update);
	});
	return setFractionCellValues(setCellValues(needs, shiftUpdates), fractionUpdates);
}

export function readSlotCounter(needs, slot) {
	if (!slot) return 0;
	if (slot.fractionKey) {
		return readFractionCounter(
			needs,
			slot.functionId,
			slot.fractionKey,
			slot.dayTypeId,
			slot.ubicationId
		);
	}
	return readCounter(needs, slot.functionId, slot.shiftId, slot.dayTypeId, slot.ubicationId);
}

export function buildFractionRows({ fractions, ubications, hasUbications }) {
	const list = fractions || [];
	if (!hasUbications) {
		return list.map((fraction) => ({ kind: "fraction", fraction, ubication: null }));
	}

	const rows = [];
	sortUbications(ubications).forEach((ubication) => {
		rows.push({ kind: "ubication", ubication });
		list.forEach((fraction) => {
			rows.push({ kind: "fraction", fraction, ubication });
		});
	});
	return rows;
}

export function isFractionGridReady({
	selectedFunctions,
	fractions,
	selectedUbications,
	hasUbications,
	dayTypes,
}) {
	if (!selectedFunctions || !selectedFunctions.length) return false;
	if (!fractions || !fractions.length) return false;
	if (!dayTypes || !dayTypes.length) return false;
	if (hasUbications && (!selectedUbications || !selectedUbications.length)) return false;
	return true;
}

export function syncStaffingNeeds({
	editorMode = "shift",
	selectedFunctions = [],
	selectedShifts = [],
	fractions = [],
	selectedUbications = [],
	dayTypes = [],
	hasUbications = false,
	previousNeeds = [],
	timeTypeId = null,
}) {
	const previous = previousNeeds || [];
	if (!dayTypes.length) return previous;
	if (!selectedFunctions.length) return [];
	if (hasUbications && !selectedUbications.length) return previous;

	const shiftPrevious = editorMode === "fraction" ? [] : previous.filter(isShiftNeed);
	const intervalPrevious = editorMode === "shift" ? [] : previous.filter(isIntervalNeed);
	const shiftNeeds =
		editorMode === "shift" && selectedShifts.length
			? rebuildNeeds({
					selectedFunctions,
					selectedShifts,
					selectedUbications,
					dayTypes,
					hasUbications,
					previousNeeds: shiftPrevious,
				})
			: [];

	const activeFractions = editorMode === "fraction" ? fractions : [];
	const fractionNeeds =
		editorMode !== "fraction"
			? []
			: activeFractions.length
				? rebuildFractionNeeds({
						selectedFunctions,
						fractions: activeFractions,
						selectedUbications,
						dayTypes,
						hasUbications,
						previousNeeds: intervalPrevious,
						timeTypeId,
					})
				: intervalPrevious;

	return [...shiftNeeds, ...fractionNeeds];
}

export function rememberFractionNeeds(memory, needs, activeFractionKeys) {
	const active = new Set(activeFractionKeys || []);
	const map = new Map();

	const stored = (need) => {
		if (!isIntervalNeed(need) || !need.function || !need.day_type) return null;
		const range = intervalLocalRange(need.time_interval);
		if (!range) return null;
		return {
			range,
			key: fractionCellKey(
				need.function.id,
				range.key,
				need.day_type.id,
				needUbicationId(need)
			),
		};
	};

	(memory || []).forEach((need) => {
		const item = stored(need);
		if (!item || normalizeCounter(need.counter) <= 0) return;
		map.set(item.key, need);
	});

	(needs || []).forEach((need) => {
		const item = stored(need);
		if (!item) return;
		const counter = normalizeCounter(need.counter);
		if (active.has(item.range.key) && counter <= 0) {
			map.delete(item.key);
			return;
		}
		if (counter > 0) map.set(item.key, need);
	});

	return [...map.values()];
}
